// ProjectFloorPlanService.js

/**
 * Firestore read/write for project-scoped floor plan layout documents.
 */

// Firebase
import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

// Utilities
import { collections, db } from 'firebaseConfig';
import {
  createEmptyFloorPlan,
  normalizeFloorPlan,
  toPersistableFloorPlan,
} from 'nodetypes/project/floorplan/projectFloorPlanModel';

/**
 * getProjectFloorPlanRef
 *
 * Returns the Firestore ref for a project's floor plan document.
 *
 * @param {string} baseKey
 * @param {string} projectKey
 * @returns {import('firebase/firestore').DocumentReference}
 */
export function getProjectFloorPlanRef(baseKey, projectKey) {
  return doc(
    db,
    collections.bases,
    baseKey,
    collections.projectFloorPlans,
    projectKey,
  );
}

/**
 * loadProjectFloorPlan
 *
 * Loads a floor plan document, or an empty layout when missing.
 *
 * @param {{ baseKey: string, projectKey: string }} params
 * @returns {Promise<object>}
 */
export async function loadProjectFloorPlan({ baseKey, projectKey }) {
  const cleanedBaseKey = String(baseKey || '').trim();
  const cleanedProjectKey = String(projectKey || '').trim();
  if (!cleanedBaseKey || !cleanedProjectKey) {
    return createEmptyFloorPlan({ baseKey: cleanedBaseKey, projectKey: cleanedProjectKey });
  }

  const snap = await getDoc(getProjectFloorPlanRef(cleanedBaseKey, cleanedProjectKey));
  if (!snap.exists()) {
    return createEmptyFloorPlan({ baseKey: cleanedBaseKey, projectKey: cleanedProjectKey });
  }

  return normalizeFloorPlan(snap.data(), {
    baseKey: cleanedBaseKey,
    projectKey: cleanedProjectKey,
  });
}

/**
 * subscribeProjectFloorPlan
 *
 * Subscribes to live floor plan updates.
 *
 * @param {{ baseKey: string, projectKey: string, onChange: Function, onError?: Function }} params
 * @returns {Function} unsubscribe
 */
export function subscribeProjectFloorPlan({
  baseKey,
  projectKey,
  onChange,
  onError,
}) {
  const cleanedBaseKey = String(baseKey || '').trim();
  const cleanedProjectKey = String(projectKey || '').trim();

  if (!cleanedBaseKey || !cleanedProjectKey || typeof onChange !== 'function') {
    return () => {};
  }

  return onSnapshot(
    getProjectFloorPlanRef(cleanedBaseKey, cleanedProjectKey),
    (snap) => {
      if (!snap.exists()) {
        onChange(createEmptyFloorPlan({
          baseKey: cleanedBaseKey,
          projectKey: cleanedProjectKey,
        }));
        return;
      }
      onChange(normalizeFloorPlan(snap.data(), {
        baseKey: cleanedBaseKey,
        projectKey: cleanedProjectKey,
      }));
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
    },
  );
}

/**
 * saveProjectFloorPlan
 *
 * Persists a floor plan layout document.
 *
 * @param {{ layout: object, updatedBy?: string|null }} params
 * @returns {Promise<object>} persisted layout
 */
export async function saveProjectFloorPlan({ layout, updatedBy = null }) {
  const payload = toPersistableFloorPlan(layout);
  const baseKey = String(payload.baseKey || '').trim();
  const projectKey = String(payload.projectKey || '').trim();
  if (!baseKey || !projectKey) {
    throw new Error('Floor plan requires baseKey and projectKey.');
  }

  const ref = getProjectFloorPlanRef(baseKey, projectKey);
  await setDoc(ref, {
    ...payload,
    updatedAt: serverTimestamp(),
    updatedBy: updatedBy || null,
  }, { merge: true });

  return payload;
}
